import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();

  const [valid, setValid] = useState(null);
  const [password, setPassword] = useState("");
  const [pw2, setPw2] = useState("");
  const [status, setStatus] = useState({ loading: false, msg: null, ok: false });

  useEffect(() => {
    let mounted = true;
    async function check() {
      if (!token) {
        setValid(false);
        return;
      }
      try {
        const res = await fetch(
          `${API}/api/auth/reset-password/validate/${encodeURIComponent(token)}`
        );
        const data = await res.json();
        if (!mounted) return;
        setValid(Boolean(data.valid));
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setValid(false);
      }
    }
    check();
    return () => {
      mounted = false;
    };
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();

    if (password.length < 8) {
      setStatus({
        loading: false,
        ok: false,
        msg: "Le mot de passe doit contenir au moins 8 caractères.",
      });
      return;
    }
    if (password !== pw2) {
      setStatus({
        loading: false,
        ok: false,
        msg: "Les mots de passe ne correspondent pas.",
      });
      return;
    }

    setStatus({ loading: true, msg: null, ok: false });

    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Erreur serveur");
      }

      setStatus({ loading: false, ok: true, msg: "Mot de passe mis à jour ✅" });
      setTimeout(() => navigate("/"), 1200);
    } catch (e) {
      setStatus({ loading: false, ok: false, msg: e.message || "Erreur" });
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden flex items-center justify-center px-4 py-12">
      {/* fond dégradé (comme ForgotPassword) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 15% 0%, rgba(99,102,241,0.18) 0, transparent 55%), radial-gradient(circle at 85% 10%, rgba(249,115,22,0.12) 0, transparent 55%)",
        }}
      />

      {/* grille légère */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.12) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(circle at 50% 10%, black 0%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(circle at 50% 10%, black 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
        <h1 className="text-xl font-semibold text-slate-900 text-center mb-1">
          Réinitialiser le mot de passe
        </h1>

        <p className="text-xs text-slate-500 text-center mb-6 leading-relaxed">
          Choisissez un nouveau mot de passe. Le lien est{" "}
          <span className="font-medium">valable 60&nbsp;minutes</span>.
        </p>

        {valid === null && (
          <div className="rounded-xl px-3 py-2 text-xs border bg-slate-50 border-slate-200 text-slate-700">
            Vérification du lien…
          </div>
        )}

        {valid === false && (
          <div className="space-y-3">
            <div className="rounded-xl px-3 py-2 text-xs border bg-red-50 border-red-200 text-red-700">
              Lien invalide ou expiré.
            </div>

            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
            >
              Demander un nouveau lien
            </Link>

            <p className="text-xs text-slate-600 text-center">
              <Link to="/login" className="text-indigo-600 hover:text-indigo-500 font-medium">
                ← Revenir à la connexion
              </Link>
            </p>
          </div>
        )}

        {valid === true && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Au moins 8 caractères"
                  className="w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3 py-2.5 pl-9 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <Lock className="absolute left-3 top-[14px] h-4 w-4 text-slate-400" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <input
                  type="password"
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Saisis de nouveau le mot de passe"
                  className="w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3 py-2.5 pl-9 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <Lock className="absolute left-3 top-[14px] h-4 w-4 text-slate-400" />
              </div>
            </div>

            {status.msg && (
              <div
                className={`rounded-xl px-3 py-2 text-xs border ${
                  status.ok
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                {status.msg}
              </div>
            )}

            <button
              type="submit"
              disabled={status.loading}
              className="
                w-full mt-2 inline-flex items-center justify-center gap-2
                rounded-xl bg-indigo-600 px-4 py-3
                text-sm font-semibold text-white
                shadow-sm
                hover:bg-indigo-500
                disabled:opacity-60
                focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-indigo-500
                focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50
                transition
              "
            >
              {status.loading ? "Mise à jour…" : "Valider"}
            </button>

            <p className="mt-5 text-xs text-slate-600 text-center">
              <Link
                to="/login"
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                ← Revenir à la connexion
              </Link>
            </p>
          </form>
        )}

        <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
          Procédure sécurisée — aucune information révélée
        </div>
      </div>
    </div>
  );
}
